import {
  CLINICIAN_ROLES,
  IBroadcastInfoMembers,
  IGroupInfoMembers,
  INearbyPatient,
  IUserInfo,
} from "@home-health-notify/domain";
import {
  DISTANCE_IN_MILES,
  IClinicianFilterType,
} from "hhn-commons";
import { useCallback, useEffect, useRef, useState } from "react";
import PersonIcon from "@mui/icons-material/Person";
import { Group as GroupIcon } from "@mui/icons-material";
import { Chip } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClinicianDisciplineChips from "./ClinicianDisciplineChips";
import DistanceChips from "./DistanceChips";
import { ClinicianList, CountsContainer, CountLabel, FilterChipContainer } from "./AdvanceSearchList.style";
import AddUserList from "../../AddUserList";
import AddMemberChips from "../../AddMemberChips";

interface ClinicianSearchResultsProps {
  onSelectDiscipline: (discipline: CLINICIAN_ROLES) => void;
  selectedDiscipline: CLINICIAN_ROLES;
  disciplinesFilters: IClinicianFilterType[];
  onSelectDistance: (distance: DISTANCE_IN_MILES) => void;
  selectedDistance: DISTANCE_IN_MILES;
  patientsCount: number;
  nearbyPatients?: INearbyPatient[];
  onFilteredPatientsChange?: (filtered: INearbyPatient[] | undefined) => void;
  isLoading: boolean;
  users: IUserInfo[];
  onScrollCallback: () => void;
  showActions?: boolean;
  existingMembers?: IGroupInfoMembers[] | IBroadcastInfoMembers[];
  existingGroup?: boolean;
  enableReferrals?: boolean;
  onAddMemberCallback?: (selectedMembers: any) => void;
  isAddMembersLoading?: boolean;
  addMembersAndCloseDialog?: () => Promise<void>;
}

const noop = () => {};

const ClinicianSearchResults = ({
  onSelectDiscipline,
  selectedDiscipline,
  disciplinesFilters,
  onSelectDistance,
  selectedDistance,
  patientsCount,
  nearbyPatients,
  onFilteredPatientsChange,
  isLoading,
  users,
  onScrollCallback,
  showActions = true,
  existingMembers,
  existingGroup,
  enableReferrals,
  onAddMemberCallback,
  isAddMembersLoading = false,
  addMembersAndCloseDialog,
}: ClinicianSearchResultsProps) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const onFilteredPatientsChangeRef = useRef(onFilteredPatientsChange);
  onFilteredPatientsChangeRef.current = onFilteredPatientsChange;

  // Reset selection when filters change or data is reset (new search)
  const isDataReset = users.length === 0;
  useEffect(() => {
    setSelectedUserId(null);
  }, [selectedDiscipline, selectedDistance, isDataReset]);

  // Update map markers when nearbyPatients change (initial load / pagination)
  useEffect(() => {
    if (!selectedUserId) {
      onFilteredPatientsChangeRef.current?.(nearbyPatients);
    }
  }, [nearbyPatients, selectedUserId]);

  const onItemClick = useCallback(
    (userId: string) => {
      setSelectedUserId(prev => {
        const next = prev === userId ? null : userId;
        if (!next || !nearbyPatients) {
          onFilteredPatientsChangeRef.current?.(nearbyPatients);
        } else {
          onFilteredPatientsChangeRef.current?.(
            nearbyPatients.filter(p =>
              p.clinicians?.some(c => c.id === next),
            ),
          );
        }
        return next;
      });
    },
    [nearbyPatients],
  );

  return (
    <>
      <ClinicianDisciplineChips
        onSelectDiscipline={onSelectDiscipline}
        selectedDiscipline={selectedDiscipline}
        options={disciplinesFilters}
      />
      <DistanceChips
        onSelectDistance={onSelectDistance}
        selectedDistance={selectedDistance}
      />
      {!isLoading && (
        <CountsContainer>
          <CountLabel>
            <PersonIcon fontSize="small" />
            {patientsCount} {patientsCount === 1 ? "Patient" : "Patients"}
          </CountLabel>
          <CountLabel>
            <GroupIcon fontSize="small" />
            {users.length} {selectedDiscipline}
          </CountLabel>
        </CountsContainer>
      )}
      {selectedUserId && (
        <FilterChipContainer>
          <Chip
            icon={<FilterListIcon />}
            label={`Filtered by: ${users.find(u => u.id === selectedUserId)?.title ?? ""}`}
            onDelete={() => onItemClick(selectedUserId)}
            size="small"
            color="primary"
            variant="outlined"
          />
        </FilterChipContainer>
      )}
      <ClinicianList>
        <AddUserList
          users={users}
          enableReferrals={enableReferrals}
          existingMembers={existingMembers}
          onScrollCallback={onScrollCallback}
          isLoading={isLoading}
          hideSearchAndFilters={true}
          existingGroup={existingGroup}
          onAddMemberCallback={onAddMemberCallback ?? noop}
          showActions={showActions}
          onItemClick={onItemClick}
          selectedUserId={selectedUserId}
        />
        {onAddMemberCallback && (
          <AddMemberChips
            isLoading={isAddMembersLoading}
            addMembersAndCloseDialog={addMembersAndCloseDialog}
            onAddMemberCallback={onAddMemberCallback}
          />
        )}
      </ClinicianList>
    </>
  );
};

export default ClinicianSearchResults;
